import { Avatar, type AvatarSubject } from '../design/Avatar';
import { Button } from '../design/Button';
import { Panel } from '../design/Panel';
import { ChevronBand, Halftone, Label, Paper, Rule } from '../design/Paper';
import { Stamp } from '../design/Stamp';
import { Tracker } from '../design/Tracker';
import { VoteCard, type CardFace } from '../design/VoteCard';
import { ro } from '../i18n/ro';
import { tiltFor } from '../design/tokens';

/**
 * Static mocks of the real screens, for judging the design before any of it is
 * wired to a live game. Not part of the app.
 *
 * Vertical rhythm on the projector comes entirely from the `scr-*` spacing
 * scale (container-relative), so every screen shares one set of gaps.
 */

const P: AvatarSubject[] = [
  { id: '1', name: 'Ana Popescu', initials: 'AP', colorIndex: 0, hasAvatar: false, avatarVersion: 0 },
  { id: '2', name: 'Bogdan Marin', initials: 'BM', colorIndex: 1, hasAvatar: false, avatarVersion: 0 },
  { id: '3', name: 'Cristina', initials: 'CR', colorIndex: 2, hasAvatar: false, avatarVersion: 0 },
  { id: '4', name: 'Ștefan Țuca', initials: 'ȘȚ', colorIndex: 3, hasAvatar: false, avatarVersion: 0 },
  { id: '5', name: 'Dan Ionescu', initials: 'DI', colorIndex: 4, hasAvatar: false, avatarVersion: 0 },
  { id: '6', name: 'Elena', initials: 'EL', colorIndex: 5, hasAvatar: false, avatarVersion: 0 },
];

/* ------------------------------------------------------------------ *
 * Projector
 * ------------------------------------------------------------------ */

function ProjectorFrame({
  kicker,
  meta,
  children,
  failedElections = 2,
}: {
  kicker: string;
  meta?: string;
  children: React.ReactNode;
  failedElections?: number;
}) {
  return (
    <Paper className="projector flex flex-col">
      <header className="relative shrink-0 bg-ink px-scr-gut py-scr-2 text-paper">
        <Halftone className="text-paper" />
        <div className="relative flex items-baseline justify-between gap-scr-gut">
          <Label className="text-screen-md">{kicker}</Label>
          <div className="flex items-baseline gap-scr-gut">
            {meta && <Label className="text-screen-md">{meta}</Label>}
            <Label className="text-screen-sm opacity-55">{ro.appName}</Label>
          </div>
        </div>
      </header>
      <ChevronBand className="shrink-0 text-ink" />

      <main className="relative flex min-h-0 flex-1 flex-col items-center justify-center gap-scr-3 overflow-hidden px-scr-gut py-scr-3">
        {children}
      </main>

      <footer className="shrink-0 px-scr-gut pb-scr-2">
        <Rule className="mb-scr-2" />
        <Tracker failedElections={failedElections} size="lg" />
      </footer>
    </Paper>
  );
}

/** The president/chancellor pair. Reads as one unit, so the inner gap is tight. */
function GovernmentBlock({
  president,
  chancellor,
  compact = false,
}: {
  president: AvatarSubject;
  chancellor: AvatarSubject;
  compact?: boolean;
}) {
  return (
    <div className={compact ? 'flex items-start justify-center gap-scr-gut' : 'flex items-start justify-center gap-scr-4'}>
      {[
        [ro.president, president],
        [ro.chancellor, chancellor],
      ].map(([role, person]) => (
        <div key={role as string} className="flex min-w-0 flex-col items-center">
          <Panel
            press={compact ? 'md' : 'lg'}
            rule
            innerClassName="p-scr-1"
            className={tiltFor(role as string)}
          >
            <Avatar
              subject={person as AvatarSubject}
              roomToken={null}
              size="hero"
              className={compact ? 'h-[13cqh] w-[13cqh]' : undefined}
            />
          </Panel>
          <div
            className={
              compact
                ? 'mt-scr-2 max-w-[22cqw] truncate font-display text-screen-md uppercase leading-none'
                : 'mt-scr-2 max-w-[34cqw] truncate font-display text-screen-lg uppercase leading-none'
            }
          >
            {(person as AvatarSubject).name}
          </div>
          <Label className="mt-scr-1 text-screen-sm text-ink-soft">{role as string}</Label>
        </div>
      ))}
    </div>
  );
}

/**
 * The government as a single horizontal band. Used on the reveal, where the
 * cards are the subject and the government is context — three portrait-sized
 * registers cannot fit a 16:9 frame, and the register that must not shrink is
 * the one carrying the votes.
 */
function GovernmentBand({
  president,
  chancellor,
}: {
  president: AvatarSubject;
  chancellor: AvatarSubject;
}) {
  return (
    <div className="flex shrink-0 items-center justify-center gap-scr-4">
      {[
        [ro.president, president],
        [ro.chancellor, chancellor],
      ].map(([role, person]) => (
        <div key={role as string} className="flex min-w-0 items-center gap-[1.2cqw]">
          <Panel press="sm" rule innerClassName="p-[0.5cqh]" className={tiltFor(role as string)}>
            <Avatar
              subject={person as AvatarSubject}
              roomToken={null}
              size="hero"
              className="h-[10cqh] w-[10cqh]"
            />
          </Panel>
          <div className="min-w-0">
            <div className="max-w-[24cqw] truncate font-display text-screen-md uppercase leading-none">
              {(person as AvatarSubject).name}
            </div>
            <Label className="mt-[0.6cqh] block text-screen-sm text-ink-soft">
              {role as string}
            </Label>
          </div>
        </div>
      ))}
    </div>
  );
}

/**
 * The row of ballot cards with each voter beneath. This is the register the
 * whole app exists for, so it gets the width and the largest card size.
 */
function BallotRow({
  players,
  faces,
  caption,
  dimmed = [],
}: {
  players: AvatarSubject[];
  faces: CardFace[];
  caption?: string;
  dimmed?: number[];
}) {
  return (
    <Panel tone="sunk" press="md" className="w-full shrink-0" innerClassName="px-scr-gut py-scr-2">
      <div className="flex items-end justify-center gap-[2.4cqw]">
        {players.map((p, i) => (
          <div key={p.id} className="flex min-w-0 flex-col items-center gap-scr-1">
            <VoteCard face={faces[i]!} size="lg" />
            <Avatar
              subject={p}
              roomToken={null}
              size="sm"
              dimmed={dimmed.includes(i)}
              className="mt-scr-1 h-[8cqh] w-[8cqh] min-h-5 min-w-5 text-[2.6cqh]"
            />
            <span className="max-w-[12cqw] truncate text-center text-screen-sm leading-tight">
              {p.name}
            </span>
          </div>
        ))}
      </div>
      {caption && (
        <div className="mt-scr-2 text-center">
          <Label className="text-screen-md">{caption}</Label>
        </div>
      )}
    </Panel>
  );
}

/** Projector, mid-vote: who has voted is public, what they chose is not. */
export function ProjectorVoting() {
  const faces: CardFace[] = ['DOWN', 'DOWN', 'DOWN', 'PENDING', 'DOWN', 'PENDING'];
  return (
    <ProjectorFrame kicker={ro.votingInProgress} meta={ro.votesCast(4, 6)}>
      <div className="flex shrink-0 flex-col items-center">
        <Label className="text-screen-sm text-ink-soft">{ro.proposedGovernment}</Label>
        <div className="mt-scr-2">
          <GovernmentBlock president={P[0]!} chancellor={P[1]!} />
        </div>
      </div>

      <BallotRow players={P} faces={faces} />
    </ProjectorFrame>
  );
}

/** Projector, revealed: every vote attributed, all at once. */
export function ProjectorRevealed() {
  const votes: CardFace[] = ['JA', 'JA', 'NEIN', 'JA', 'NEIN', 'JA'];
  return (
    <ProjectorFrame kicker={ro.electedGovernment} failedElections={0}>
      <div className="flex w-full shrink-0 items-center justify-center gap-scr-4">
        <Stamp tone="teal" size="lg" className={`shrink-0 ${tiltFor('approved')}`}>
          {ro.governmentApproved}
        </Stamp>
        <div className="flex shrink-0 items-baseline gap-[1.2cqw] font-display leading-none">
          <span className="text-[7cqh] text-teal">4</span>
          <span className="text-screen-md text-ink-faint">—</span>
          <span className="text-[7cqh] text-red">2</span>
        </div>
      </div>

      <GovernmentBand president={P[0]!} chancellor={P[1]!} />

      <BallotRow players={P} faces={votes} dimmed={[4]} />
    </ProjectorFrame>
  );
}

/** Projector, chaos: takes over the display until the next session starts. */
export function ProjectorChaos() {
  return (
    <Paper className="projector flex flex-col">
      <div className="relative flex min-h-0 flex-1 flex-col items-center justify-center gap-scr-4 overflow-hidden bg-red px-scr-gut text-center text-paper">
        <Halftone className="text-paper opacity-30" />
        <div className="chevron-band absolute inset-x-0 top-0 h-[2.2cqh] text-paper opacity-40" />
        <div className="chevron-band absolute inset-x-0 bottom-0 h-[2.2cqh] text-paper opacity-40" />

        <h1 className="relative max-w-[92cqw] font-display text-screen-hero uppercase leading-[0.95]">
          {ro.chaosTitle}
        </h1>
        <p className="relative max-w-[52cqw] text-balance text-screen-md leading-relaxed opacity-90">
          {ro.chaosBody}
        </p>
      </div>
      <footer className="shrink-0 bg-paper px-scr-gut py-scr-2">
        <Tracker failedElections={3} size="lg" />
      </footer>
    </Paper>
  );
}

/**
 * The roster has to hold five to fifteen people without overflowing a fixed
 * 16:9 frame, so the column count comes from the headcount and every entry
 * stays on one line.
 */
function Roster({ players }: { players: AvatarSubject[] }) {
  const columns = players.length > 8 ? 3 : 2;

  return (
    <div
      className="grid w-full content-center gap-[1cqw]"
      style={{ gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))` }}
    >
      {players.map((p) => (
        <Panel
          key={p.id}
          press="sm"
          className={tiltFor(p.id)}
          innerClassName="flex min-w-0 items-center gap-[1cqw] px-[1cqw] py-scr-1"
        >
          <Avatar
            subject={p}
            roomToken={null}
            size="sm"
            className="h-[7cqh] w-[7cqh] min-h-5 min-w-5 text-[2.2cqh]"
          />
          <span className="min-w-0 flex-1 truncate font-display text-screen-sm uppercase">
            {p.name}
          </span>
        </Panel>
      ))}
    </div>
  );
}

/** Projector, lobby: the join QR beside the roster, both optically centred. */
export function ProjectorLobby() {
  return (
    <ProjectorFrame kicker={ro.waitingToStart} failedElections={0}>
      <div className="flex min-h-0 w-full flex-1 items-center justify-center gap-scr-4">
        <div className="flex shrink-0 flex-col items-center gap-scr-2">
          <Label className="whitespace-nowrap text-center text-screen-sm">{ro.scanToJoin}</Label>
          <Panel press="lg" rule innerClassName="p-scr-1">
            {/* Stand-in for the generated QR. */}
            <div
              className="h-[50cqh] w-[50cqh] min-h-12 min-w-12 bg-ink"
              style={{
                backgroundImage:
                  'repeating-conic-gradient(var(--color-ink) 0% 25%, var(--color-paper-raised) 0% 50%)',
                backgroundSize: '9% 9%',
              }}
            />
          </Panel>
        </div>

        <div className="flex min-h-0 min-w-0 flex-1 flex-col justify-center gap-scr-2 self-stretch">
          <Label className="shrink-0 text-screen-sm text-ink-soft">
            {ro.players} · {P.length}
          </Label>
          <Roster players={P} />
        </div>
      </div>
    </ProjectorFrame>
  );
}

/* ------------------------------------------------------------------ *
 * Phone
 * ------------------------------------------------------------------ */

function PhoneFrame({ children }: { children: React.ReactNode }) {
  return (
    <Paper className="flex h-full flex-col" vignette={false}>
      <header className="relative shrink-0 bg-ink px-4 py-3 text-paper">
        <Halftone className="text-paper" />
        <div className="relative flex items-center justify-between gap-3">
          <Label className="text-[0.6rem] opacity-70">{ro.appName}</Label>
          <Tracker failedElections={2} size="sm" className="[&_span]:text-paper/70" />
        </div>
      </header>
      <ChevronBand className="shrink-0 text-ink" />
      {children}
    </Paper>
  );
}

/** Phone, voting: the proposed government, then two very large choices. */
export function PhoneVoting() {
  return (
    <PhoneFrame>
      <main className="flex min-h-0 flex-1 flex-col px-4 pb-5 pt-5">
        {/* The government is optically centred in the space above the targets,
            rather than pinned to the top with a void beneath it. */}
        <div className="flex min-h-0 flex-1 flex-col items-center justify-center gap-4">
          <Label className="text-center text-[0.65rem] text-ink-soft">
            {ro.proposedGovernment}
          </Label>

          <div className="grid w-full grid-cols-2 gap-3">
            {[
              [ro.president, P[0]!],
              [ro.chancellor, P[1]!],
            ].map(([role, person]) => (
              <Panel key={role as string} press="sm" rule innerClassName="p-3 text-center">
                <Avatar
                  subject={person as AvatarSubject}
                  roomToken={null}
                  size="lg"
                  className="mx-auto h-28 w-28 text-3xl"
                />
                <div className="mt-3 truncate font-display text-sm uppercase">
                  {(person as AvatarSubject).name}
                </div>
                <Label className="mt-1 block text-[0.55rem] text-ink-faint">{role as string}</Label>
              </Panel>
            ))}
          </div>

          <div className="mt-1 flex flex-col items-center gap-2">
            <Label className="text-[0.6rem] text-ink-soft">{ro.votesCast(4, 6)}</Label>
            <div className="flex flex-wrap justify-center gap-2">
              {P.map((p, i) => (
                <Avatar key={p.id} subject={p} roomToken={null} size="sm" dimmed={i >= 4} />
              ))}
            </div>
          </div>
        </div>

        <div className="mt-6 grid shrink-0 grid-cols-2 gap-4">
          <Button tone="teal" size="vote" block>{ro.ja}</Button>
          <Button tone="red" size="vote" block>{ro.nein}</Button>
        </div>
      </main>
    </PhoneFrame>
  );
}

/** Phone, after voting: own choice locked, waiting on the rest. */
export function PhoneVoted() {
  return (
    <PhoneFrame>
      <main className="flex min-h-0 flex-1 flex-col px-4 pb-5 pt-6">
        <div className="flex min-h-0 flex-1 flex-col items-center justify-center gap-5">
          <Label className="text-[0.65rem] text-ink-soft">{ro.yourVote}</Label>
          <VoteCard face="JA" size="md" className="h-40 w-28 text-2xl" />
          <Stamp tone="ink" size="sm" className={tiltFor('locked')}>
            {ro.hasVoted}
          </Stamp>
        </div>

        <Panel tone="sunk" press="sm" className="mt-5 shrink-0" innerClassName="px-3 py-4">
          <Label className="block text-center text-[0.6rem] text-ink-soft">
            {ro.waitingForVotes}
          </Label>
          <div className="mt-3 flex flex-wrap justify-center gap-2">
            {P.map((p, i) => (
              <Avatar key={p.id} subject={p} roomToken={null} size="sm" dimmed={i >= 4} />
            ))}
          </div>
          <div className="mt-3 text-center">
            <Label className="text-xs">{ro.votesCast(4, 6)}</Label>
          </div>
        </Panel>
      </main>
    </PhoneFrame>
  );
}

/** Phone, joining: name and optional photo. */
export function PhoneJoin() {
  return (
    <PhoneFrame>
      <main className="flex min-h-0 flex-1 flex-col px-4 pb-5 pt-6">
        <div className="flex min-h-0 flex-1 flex-col gap-6">
          <div>
            <h1 className="font-display text-2xl uppercase leading-tight">{ro.chooseName}</h1>
            <Panel press="sm" className="mt-3" innerClassName="px-3 py-3">
              <span className="text-base text-ink-faint">{ro.namePlaceholder}</span>
            </Panel>
          </div>

          <div>
            <Label className="block text-[0.6rem] text-ink-soft">{ro.photoSection}</Label>
            <div className="mt-3 flex items-center gap-3">
              <Panel press="sm" rule innerClassName="p-1.5">
                <Avatar
                  subject={{
                    id: 'x',
                    name: 'Ana Popescu',
                    initials: 'AP',
                    colorIndex: 0,
                    hasAvatar: false,
                    avatarVersion: 0,
                  }}
                  roomToken={null}
                  size="lg"
                />
              </Panel>
              <div className="flex flex-1 flex-col gap-2">
                <Button tone="paper" size="sm" block>{ro.takePhoto}</Button>
                <Button tone="ghost" size="sm" block>{ro.addPhoto}</Button>
              </div>
            </div>
          </div>
        </div>

        <div className="mt-6 shrink-0">
          <Button tone="ink" size="lg" block>{ro.join}</Button>
        </div>
      </main>
    </PhoneFrame>
  );
}

/** Phone, admin: dense on purpose — the admin wrote the app. */
export function PhoneAdmin() {
  return (
    <PhoneFrame>
      <main className="flex min-h-0 flex-1 flex-col gap-5 overflow-hidden px-4 pb-5 pt-5">
        <div>
          <Label className="block text-[0.6rem] text-ink-soft">{ro.adminPanel}</Label>
          {/* Equal-height cells: Romanian labels differ enough in length that a
              free-flowing grid comes out ragged. */}
          <div className="mt-3 grid grid-cols-2 gap-2">
            <Button tone="ink" size="sm" block>{ro.startVote}</Button>
            <Button tone="red" size="sm" block>{ro.annulVote}</Button>
            <Button tone="paper" size="sm" block>{ro.setGovernment}</Button>
            <Button tone="paper" size="sm" block>{ro.showHistory}</Button>
          </div>
        </div>

        <Panel tone="sunk" press="sm" className="min-h-0 flex-1" innerClassName="flex flex-col p-3">
          <Label className="block shrink-0 text-[0.6rem] text-ink-soft">{ro.choosePresident}</Label>
          <div className="mt-3 flex min-h-0 flex-col gap-1 overflow-hidden">
            {P.slice(0, 5).map((p, i) => (
              <div
                key={p.id}
                className="flex items-center gap-2.5 border-b border-ink/15 py-2 last:border-0"
              >
                <Avatar subject={p} roomToken={null} size="sm" dimmed={i === 3} />
                <span className="min-w-0 flex-1 truncate text-sm">{p.name}</span>
                {i === 1 && (
                  <Stamp tone="ink" size="sm" className="shrink-0">
                    {ro.termLimited}
                  </Stamp>
                )}
                {i === 3 && (
                  <Stamp tone="red" size="sm" className="shrink-0">
                    {ro.executed}
                  </Stamp>
                )}
              </div>
            ))}
          </div>
        </Panel>
      </main>
    </PhoneFrame>
  );
}
